import React, { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  Box,
  Typography,
  Autocomplete,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import { useTSMInviteUser } from "../../hooks/useTSRegistration";
import {
  validateEmailDomain,
  getEmailDomainError,
} from "../../utils/passwordUtils";
import { UserInvitationCreate } from "../../types/registrationTypes";
import {
  TypedSelectOption,
  YupTypedSelectOption,
} from "../../types/common/selectOptions";

type EmpOption = TypedSelectOption<
  "employee",
  { first: string; last: string; email: string }
>;

// Validation schema
const inviteUserSchema = yup.object({
  employee: YupTypedSelectOption.required("Please select an employee"),
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address")
    .test(
      "domain",
      "Only @centese.com email addresses are allowed",
      (value) => {
        return value ? validateEmailDomain(value) : false;
      },
    ),
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  notes: yup
    .string()
    .optional()
    .max(500, "Notes must be less than 500 characters"),
});

interface InviteUserFormData {
  employee: EmpOption | null;
  email: string;
  first_name: string;
  last_name: string;
  notes?: string;
}

interface InviteUserFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const InviteUserForm: React.FC<InviteUserFormProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  // State
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    data: empsResponse,
    isLoading: empsLoading,
    isFetching: empsFetching,
    error: empsError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "employees",
      alt_name: "emp",
      primary_fields: ["id", "first_name", "last_name", "email"],
      joins: [
        {
          table: "users",
          alt_name: null,
          fields: ["id", "employee_id"],
          condition: {
            remote_table: "employees",
            alt_name: "emp",
            remote_field: "id",
            local_field: "employee_id",
          },
          outer: true,
        },
      ],
      sorts: [
        {
          table: "employees",
          alt_name: "emp",
          field: "first_name",
          asc: true,
        },
      ],
      filters: [
        [
          {
            table: "users",
            alt_name: null,
            field: "employee_id",
            operator: "==",
            value: null,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
          {
            table: "employees",
            alt_name: "emp",
            field: "is_record_active",
            operator: "==",
            value: true,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      subqueries: [],
    },
    true,
  );
  const empOpts: EmpOption[] = useMemo(() => {
    if (empsResponse?.items) {
      return empsResponse.items.map((emp) => ({
        label: `${emp["emp.first_name"]} ${emp["emp.last_name"]}`,
        type: "employee",
        value: {
          id: emp["emp.id"],
          first: emp["emp.first_name"],
          last: emp["emp.last_name"],
          email: emp["emp.email"],
        },
      }));
    } else {
      return [];
    }
  }, [empsResponse]);

  // Invite user mutation
  const inviteUserMutation = useTSMInviteUser();

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<InviteUserFormData>({
    resolver: yupResolver(inviteUserSchema) as any,
    defaultValues: {
      employee: null,
      email: "",
      first_name: "",
      last_name: "",
      notes: "",
    },
  });

  const watchNotes = watch("notes");

  // Handle employee selection - pre-fill email and names
  const handleEmployeeChange = (employee: EmpOption | null) => {
    if (employee) {
      setValue("email", employee.value.email || "");
      setValue("first_name", employee.value.first || "");
      setValue("last_name", employee.value.last || "");
    } else {
      setValue("email", "");
      setValue("first_name", "");
      setValue("last_name", "");
    }
  };

  // Form submission
  const onSubmit = async (data: InviteUserFormData) => {
    try {
      setError(null);
      setSuccess(null);

      const invitationData: UserInvitationCreate = {
        employee_id: data.employee!.value.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        notes: data.notes || undefined,
      };

      const result = await inviteUserMutation.mutateAsync(invitationData);

      setSuccess(`Invitation sent successfully to ${result.email}`);

      // Reset form after short delay
      setTimeout(() => {
        reset();
        setSuccess(null);
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      setError(err.message || "Failed to send invitation");
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isSubmitting && !inviteUserMutation.isPending) {
      reset();
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: 600 },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon />
            <Typography variant="h6">Invite User</Typography>
          </Stack>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Success Alert */}
          {success && <Alert severity="success">{success}</Alert>}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Instructions */}
          <Box>
            <Typography variant="body1" gutterBottom>
              Send an invitation to create a user account for an employee.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only employees without existing user accounts are available for
              selection.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Employee Selection */}
              <Controller
                name="employee"
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <Autocomplete
                    {...restField}
                    value={value}
                    onChange={(e, v) => {
                      onChange(v);
                      handleEmployeeChange(v);
                    }}
                    options={empOpts}
                    getOptionLabel={(employee: EmpOption) =>
                      employee?.label || ""
                    }
                    loading={empsLoading || empsFetching}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Employee"
                        error={!!errors.employee}
                        helperText={
                          errors.employee?.message ||
                          "Choose an employee to invite"
                        }
                        required
                      />
                    )}
                  />
                )}
              />

              {/* Email Field */}
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    required
                    error={!!errors.email}
                    helperText={
                      errors.email?.message ||
                      "Must be a @centese.com email address"
                    }
                  />
                )}
              />

              {/* Name Fields */}
              <Stack direction="row" spacing={2}>
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      error={!!errors.first_name}
                      helperText={
                        errors.first_name?.message ||
                        "Optional - will pre-fill registration form"
                      }
                    />
                  )}
                />
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message || ""}
                    />
                  )}
                />
              </Stack>

              {/* Notes Field */}
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.notes}
                    helperText={
                      errors.notes?.message ||
                      `Optional notes about this invitation (${watchNotes?.length || 0}/500 characters)`
                    }
                  />
                )}
              />
            </Stack>
          </form>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting || inviteUserMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting || inviteUserMutation.isPending}
          startIcon={<PersonAddIcon />}
        >
          {isSubmitting || inviteUserMutation.isPending
            ? "Sending Invitation..."
            : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
